import { FC } from 'react';
import { useNavigate } from '@tanstack/react-router';
import useDrawer from '@/hooks/use-drawer.hook';
import useModal from '@/hooks/use-modal.hook';
import useToast from '@/hooks/use-toast.hook';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import { deletePolicy } from '@/lib/services/policy.service';
import MoreMenu from '@/components/more-menu';
import MenuItem from '@/components/menu/menu-item';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';

interface Props {
  policy: PolicyModel;
}

const PolicyPageMenu: FC<Props> = ({ policy }) => {
  const { openDrawer } = useDrawer();
  const { openModal } = useModal();
  const { toast, errorToast } = useToast();
  const navigate = useNavigate();

  const handleEdit = () => {
    openDrawer('policy', { policyId: policy.id });
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${policy.name}?`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deletePolicy(policy.id).then(({ deleted, error }) => {
            if (deleted) {
              toast('The policy was deleted');
              refetchQuery(QueryKey.ScopedPoliciesDataset);
              navigate({ to: '/config/policy/scoped' });
              return;
            }

            errorToast(error || 'The policy was not deleted');
          });
        }
      },
    });
  };

  const canDelete = !policy.isDefault && !policy.isOverride;

  return (
    <MoreMenu>
      <MenuItem label="Edit" onClick={handleEdit} icon="edit" />
      <MenuItem
        label="Delete"
        onClick={handleDelete}
        icon="delete"
        color="error"
        disabled={!canDelete}
      />
    </MoreMenu>
  );
};

export default PolicyPageMenu;
